/**
 * IndexedDB 数据库服务
 * 负责处理提示词、文件夹和标签的存储和检索
 */

// 数据库配置
const DB_NAME = 'PromptProDB';
const DB_VERSION = 3; // 升级数据库版本从2改为3，匹配已存在的数据库版本
const STORES = {
  PROMPTS: 'prompts',
  FOLDERS: 'folders',
  TAGS: 'tags',
  USAGE_STATS: 'usageStats' // 新增使用统计存储
};

/**
 * 初始化数据库
 * @returns {Promise} 返回一个Promise，解析为数据库实例
 */
const initDB = () => {
  return new Promise((resolve, reject) => {
    try {
      // 打开数据库连接
      const request = indexedDB.open(DB_NAME, DB_VERSION);
      
      // 数据库升级事件（首次创建或版本更新时触发）
      request.onupgradeneeded = (event) => {
        const db = event.target.result;
        
        // 创建提示词存储
        if (!db.objectStoreNames.contains(STORES.PROMPTS)) {
          db.createObjectStore(STORES.PROMPTS, { keyPath: 'id' });
        }
        
        // 创建文件夹存储
        if (!db.objectStoreNames.contains(STORES.FOLDERS)) {
          db.createObjectStore(STORES.FOLDERS, { keyPath: 'id' });
        }
        
        // 创建标签存储
        if (!db.objectStoreNames.contains(STORES.TAGS)) {
          db.createObjectStore(STORES.TAGS, { keyPath: 'id' });
        }
        
        // 创建使用统计存储
        if (!db.objectStoreNames.contains(STORES.USAGE_STATS)) {
          db.createObjectStore(STORES.USAGE_STATS, { keyPath: 'id' });
        }
      };
      
      // 成功打开数据库
      request.onsuccess = (event) => {
        const db = event.target.result;
        console.log(`数据库 ${DB_NAME} (版本 ${DB_VERSION}) 初始化成功`);
        
        // 监听数据库连接错误
        db.onerror = (event) => {
          console.error('数据库错误:', event.target.error);
        };
        
        resolve(db);
      };
      
      // 打开数据库失败
      request.onerror = (event) => {
        const error = event.target.error;
        console.error(`打开数据库 ${DB_NAME} 失败:`, {
          message: error.message || '未知错误',
          name: error.name,
          code: error.code,
          stack: error.stack
        });
        reject(error);
      };
      
      // 数据库被阻塞（可能是另一个标签页正在使用）
      request.onblocked = (event) => {
        console.warn('数据库操作被阻塞，可能是另一个标签页正在使用数据库');
        // 尝试关闭所有其他连接
        try {
          const db = event.target.result;
          if (db) {
            db.close();
          }
        } catch (e) {
          console.error('关闭被阻塞的数据库连接失败:', e);
        }
      };
    } catch (error) {
      console.error('初始化数据库时发生意外错误:', {
        message: error.message || '未知错误',
        name: error.name,
        stack: error.stack
      });
      reject(error);
    }
  });
};

/**
 * 获取数据库连接
 * @returns {Promise} 返回一个Promise，解析为数据库实例
 */
const getDB = () => {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);
    
    request.onsuccess = (event) => {
      resolve(event.target.result);
    };
    
    request.onerror = (event) => {
      reject(event.target.error);
    };
  });
};

/**
 * 从localStorage迁移数据到IndexedDB
 * @returns {Promise} 返回一个Promise，解析为迁移结果
 */
const migrateFromLocalStorage = async () => {
  try {
    // 获取localStorage中的数据
    const promptsData = localStorage.getItem('prompts');
    const foldersData = localStorage.getItem('folders');
    const tagsData = localStorage.getItem('tags');
    
    // 解析数据
    const prompts = promptsData ? JSON.parse(promptsData) : [];
    const folders = foldersData ? JSON.parse(foldersData) : [];
    const tags = tagsData ? JSON.parse(tagsData) : [];
    
    // 保存到IndexedDB
    if (prompts.length > 0) {
      await saveAllItems(STORES.PROMPTS, prompts);
    }
    
    if (folders.length > 0) {
      await saveAllItems(STORES.FOLDERS, folders);
    }
    
    if (tags.length > 0) {
      await saveAllItems(STORES.TAGS, tags);
    }
    
    console.log('数据迁移完成');
    return { success: true, message: '数据迁移成功' };
  } catch (error) {
    console.error('数据迁移失败:', error);
    return { success: false, message: '数据迁移失败', error };
  }
};

/**
 * 保存单个项目到存储
 * @param {string} storeName 存储名称
 * @param {Object} item 要保存的项目
 * @returns {Promise} 返回一个Promise，解析为保存结果
 */
const saveItem = async (storeName, item) => {
  try {
    const db = await getDB();
    return new Promise((resolve, reject) => {
      const transaction = db.transaction(storeName, 'readwrite');
      const store = transaction.objectStore(storeName);
      const request = store.put(item);
      
      request.onsuccess = () => {
        resolve({ success: true, id: item.id });
      };
      
      request.onerror = (event) => {
        reject(event.target.error);
      };
    });
  } catch (error) {
    console.error(`保存到 ${storeName} 失败:`, error);
    throw error;
  }
};

/**
 * 批量保存项目到存储
 * @param {string} storeName 存储名称
 * @param {Array} items 要保存的项目数组
 * @returns {Promise} 返回一个Promise，解析为保存结果
 */
const saveAllItems = async (storeName, items) => {
  try {
    const db = await getDB();
    return new Promise((resolve, reject) => {
      const transaction = db.transaction(storeName, 'readwrite');
      const store = transaction.objectStore(storeName);
      
      let completed = 0;
      let errors = [];
      
      items.forEach(item => {
        const request = store.put(item);
        
        request.onsuccess = () => {
          completed++;
          if (completed === items.length) {
            if (errors.length > 0) {
              reject({ message: '部分项目保存失败', errors });
            } else {
              resolve({ success: true, count: items.length });
            }
          }
        };
        
        request.onerror = (event) => {
          completed++;
          errors.push({ item, error: event.target.error });
          
          if (completed === items.length) {
            reject({ message: '部分项目保存失败', errors });
          }
        };
      });
      
      // 如果没有项目要保存，直接返回成功
      if (items.length === 0) {
        resolve({ success: true, count: 0 });
      }
    });
  } catch (error) {
    console.error(`批量保存到 ${storeName} 失败:`, error);
    throw error;
  }
};

/**
 * 获取存储中的所有项目
 * @param {string} storeName 存储名称
 * @returns {Promise} 返回一个Promise，解析为项目数组
 */
const getAllItems = async (storeName) => {
  try {
    const db = await getDB();
    console.log(`正在从 ${storeName} 存储中获取所有项目...`);
    
    return new Promise((resolve, reject) => {
      try {
        // 检查存储是否存在
        if (!db.objectStoreNames.contains(storeName)) {
          console.error(`存储 ${storeName} 不存在`);
          // 如果存储不存在，返回空数组而不是抛出错误
          resolve([]);
          return;
        }
        
        const transaction = db.transaction(storeName, 'readonly');
        const store = transaction.objectStore(storeName);
        const request = store.getAll();
        
        request.onsuccess = (event) => {
          const results = event.target.result || [];
          console.log(`成功从 ${storeName} 获取 ${results.length} 条记录`);
          resolve(results);
        };
        
        request.onerror = (event) => {
          const error = event.target.error;
          console.error(`从 ${storeName} 获取数据失败:`, {
            message: error.message || '未知错误',
            name: error.name,
            code: error.code,
            stack: error.stack
          });
          reject(error);
        };
        
        // 确保事务完成
        transaction.oncomplete = () => {
          console.log(`${storeName} 数据获取事务已完成`);
        };
        
        transaction.onerror = (event) => {
          const error = event.target.error;
          console.error(`${storeName} 数据获取事务失败:`, {
            message: error.message || '未知错误',
            name: error.name,
            code: error.code,
            stack: error.stack
          });
        };
      } catch (innerError) {
        console.error(`在事务处理中发生错误 (${storeName}):`, {
          message: innerError.message || '未知错误',
          name: innerError.name,
          stack: innerError.stack
        });
        reject(innerError);
      }
    });
  } catch (error) {
    console.error(`获取 ${storeName} 所有项目失败:`, {
      message: error.message || '未知错误',
      name: error.name,
      stack: error.stack
    });
    
    // 如果是因为数据库未初始化导致的错误，尝试重新初始化
    if (error.name === 'InvalidStateError' || error.message.includes('database connection closed')) {
      console.log('尝试重新初始化数据库...');
      try {
        await initDB();
        // 重新尝试获取数据
        return getAllItems(storeName);
      } catch (reinitError) {
        console.error('重新初始化数据库失败:', reinitError);
      }
    }
    
    // 返回空数组而不是抛出错误，这样应用可以继续运行
    console.log(`从 ${storeName} 获取数据失败，返回空数组`);
    return [];
  }
};

/**
 * 获取存储中的单个项目
 * @param {string} storeName 存储名称
 * @param {string} id 项目ID
 * @returns {Promise} 返回一个Promise，解析为项目对象
 */
const getItem = async (storeName, id) => {
  try {
    const db = await getDB();
    return new Promise((resolve, reject) => {
      const transaction = db.transaction(storeName, 'readonly');
      const store = transaction.objectStore(storeName);
      const request = store.get(id);
      
      request.onsuccess = (event) => {
        resolve(event.target.result);
      };
      
      request.onerror = (event) => {
        reject(event.target.error);
      };
    });
  } catch (error) {
    console.error(`获取 ${storeName} 项目失败:`, error);
    throw error;
  }
};

/**
 * 从存储中删除项目
 * @param {string} storeName 存储名称
 * @param {string} id 项目ID
 * @returns {Promise} 返回一个Promise，解析为删除结果
 */
const deleteItem = async (storeName, id) => {
  try {
    console.log(`尝试删除 ${storeName} 中的项目，ID: ${id}`);
    
    // 确保ID是有效的
    if (!id) {
      console.error('删除失败: ID为空');
      throw new Error('删除失败: ID不能为空');
    }
    
    const db = await getDB();
    return new Promise((resolve, reject) => {
      const transaction = db.transaction(storeName, 'readwrite');
      
      // 添加事务完成和错误处理
      transaction.oncomplete = () => {
        console.log(`删除 ${storeName} 项目事务完成`);
      };
      
      transaction.onerror = (event) => {
        console.error(`删除 ${storeName} 项目事务错误:`, event.target.error);
        reject(event.target.error);
      };
      
      const store = transaction.objectStore(storeName);
      
      // 先检查项目是否存在
      const checkRequest = store.get(id);
      
      checkRequest.onsuccess = (event) => {
        const item = event.target.result;
        if (!item) {
          console.warn(`要删除的项目不存在，ID: ${id}`);
          // 如果项目不存在，我们仍然返回成功，因为最终结果是一样的
          resolve({ success: true, message: '项目不存在，无需删除' });
          return;
        }
        
        // 项目存在，执行删除
        const deleteRequest = store.delete(id);
        
        deleteRequest.onsuccess = () => {
          console.log(`成功删除 ${storeName} 中的项目，ID: ${id}`);
          resolve({ success: true });
        };
        
        deleteRequest.onerror = (event) => {
          console.error(`删除 ${storeName} 项目请求错误:`, event.target.error);
          reject(event.target.error);
        };
      };
      
      checkRequest.onerror = (event) => {
        console.error(`检查 ${storeName} 项目是否存在时出错:`, event.target.error);
        reject(event.target.error);
      };
    });
  } catch (error) {
    console.error(`删除 ${storeName} 项目失败:`, error);
    throw error;
  }
};

/**
 * 清空存储
 * @param {string} storeName 存储名称
 * @returns {Promise} 返回一个Promise，解析为清空结果
 */
const clearStore = async (storeName) => {
  try {
    const db = await getDB();
    return new Promise((resolve, reject) => {
      const transaction = db.transaction(storeName, 'readwrite');
      const store = transaction.objectStore(storeName);
      const request = store.clear();
      
      request.onsuccess = () => {
        resolve({ success: true });
      };
      
      request.onerror = (event) => {
        reject(event.target.error);
      };
    });
  } catch (error) {
    console.error(`清空 ${storeName} 失败:`, error);
    throw error;
  }
};

/**
 * 更新存储中的项目
 * @param {string} storeName 存储名称
 * @param {Object} item 要更新的项目（必须包含id字段）
 * @returns {Promise} 返回一个Promise，解析为更新结果
 */
const updateItem = async (storeName, item) => {
  try {
    if (!item || !item.id) {
      throw new Error('更新项目必须包含id字段');
    }
    
    const db = await getDB();
    return new Promise((resolve, reject) => {
      const transaction = db.transaction(storeName, 'readwrite');
      const store = transaction.objectStore(storeName);
      const request = store.put(item); // put方法会更新已存在的项目或添加新项目
      
      request.onsuccess = (event) => {
        resolve({ success: true, id: event.target.result });
      };
      
      request.onerror = (event) => {
        console.error(`更新 ${storeName} 项目失败:`, event.target.error);
        reject(event.target.error);
      };
    });
  } catch (error) {
    console.error(`更新 ${storeName} 项目失败:`, error);
    throw error;
  }
};

/**
 * 记录提示词使用
 * @param {string} promptId 提示词ID
 * @returns {Promise} 返回一个Promise，解析为更新结果
 */
const recordPromptUsage = async (promptId) => {
  try {
    const now = new Date();
    const dayOfWeek = now.getDay(); // 0-6 (周日-周六)
    const hour = now.getHours(); // 0-23
    
    // 获取当前使用统计
    const usageStats = await getItem(STORES.USAGE_STATS, promptId) || {
      id: promptId,
      viewCount: 0,
      lastViewed: null,
      weekdayHourHeatmap: Array(7).fill().map(() => Array(24).fill(0)), // 7天 x 24小时
      weeklyViews: []
    };
    
    // 更新统计数据
    usageStats.viewCount += 1;
    usageStats.lastViewed = now.toISOString();
    usageStats.weekdayHourHeatmap[dayOfWeek][hour] += 1;
    
    // 添加到周视图记录
    const weekStart = new Date(now);
    weekStart.setHours(0, 0, 0, 0);
    weekStart.setDate(now.getDate() - now.getDay()); // 设置为本周日
    
    const weekKey = weekStart.toISOString().split('T')[0];
    const weekEntry = usageStats.weeklyViews.find(entry => entry.week === weekKey);
    
    if (weekEntry) {
      weekEntry.count += 1;
    } else {
      usageStats.weeklyViews.push({
        week: weekKey,
        count: 1
      });
      
      // 只保留最近12周的数据
      if (usageStats.weeklyViews.length > 12) {
        usageStats.weeklyViews.sort((a, b) => b.week.localeCompare(a.week));
        usageStats.weeklyViews = usageStats.weeklyViews.slice(0, 12);
      }
    }
    
    // 保存更新后的统计数据
    return await saveItem(STORES.USAGE_STATS, usageStats);
  } catch (error) {
    console.error('记录提示词使用失败:', error);
    throw error;
  }
};

/**
 * 获取提示词使用统计
 * @param {string} promptId 提示词ID
 * @returns {Promise} 返回一个Promise，解析为使用统计
 */
const getPromptUsageStats = async (promptId) => {
  try {
    return await getItem(STORES.USAGE_STATS, promptId) || {
      id: promptId,
      viewCount: 0,
      lastViewed: null,
      weekdayHourHeatmap: Array(7).fill().map(() => Array(24).fill(0)),
      weeklyViews: []
    };
  } catch (error) {
    console.error('获取提示词使用统计失败:', error);
    throw error;
  }
};

/**
 * 获取所有提示词的使用统计
 * @returns {Promise} 返回一个Promise，解析为所有使用统计
 */
const getAllPromptUsageStats = async () => {
  try {
    return await getAllItems(STORES.USAGE_STATS);
  } catch (error) {
    console.error('获取所有提示词使用统计失败:', error);
    throw error;
  }
};

/**
 * 获取最常用的提示词排行榜
 * @param {number} limit 限制返回数量
 * @returns {Promise} 返回一个Promise，解析为排行榜数据
 */
const getMostUsedPrompts = async (limit = 10) => {
  try {
    const stats = await getAllPromptUsageStats();
    const prompts = await getAllItems(STORES.PROMPTS);
    
    // 将使用统计与提示词信息合并
    const usageWithPromptInfo = [];
    
    for (const stat of stats) {
      const prompt = prompts.find(p => p.id === stat.id);
      if (prompt) {
        usageWithPromptInfo.push({
          ...stat,
          title: prompt.title,
          content: prompt.content,
          tags: prompt.tags,
          folderId: prompt.folderId,
          copyCount: stat.copyCount || 0 // 确保复制次数字段存在
        });
      }
    }
    
    // 按复制次数排序，而不是查看次数
    usageWithPromptInfo.sort((a, b) => b.copyCount - a.copyCount);
    
    // 返回前N个
    return usageWithPromptInfo.slice(0, limit);
  } catch (error) {
    console.error('获取最常用提示词排行榜失败:', error);
    throw error;
  }
};

/**
 * 获取一周内使用热度地图数据
 * @returns {Promise} 返回一个Promise，解析为热度地图数据
 */
const getWeeklyHeatmapData = async () => {
  try {
    const stats = await getAllPromptUsageStats();
    
    // 合并所有提示词的热度地图
    const combinedHeatmap = Array(7).fill().map(() => Array(24).fill(0));
    
    for (const stat of stats) {
      for (let day = 0; day < 7; day++) {
        for (let hour = 0; hour < 24; hour++) {
          combinedHeatmap[day][hour] += stat.weekdayHourHeatmap[day][hour];
        }
      }
    }
    
    return combinedHeatmap;
  } catch (error) {
    console.error('获取周使用热度地图失败:', error);
    throw error;
  }
};

/**
 * 获取应用统计数据
 * @returns {Promise} 返回一个Promise，解析为统计数据
 */
const getStats = async () => {
  try {
    const prompts = await getAllItems(STORES.PROMPTS);
    const tags = await getAllItems(STORES.TAGS);
    const folders = await getAllItems(STORES.FOLDERS);
    
    // 统计标签使用情况
    const tagUsage = {};
    prompts.forEach(prompt => {
      if (prompt.tags && Array.isArray(prompt.tags)) {
        prompt.tags.forEach(tagId => {
          tagUsage[tagId] = (tagUsage[tagId] || 0) + 1;
        });
      }
    });
    
    // 获取使用最多的标签
    const tagUsageArray = Object.entries(tagUsage).map(([tagId, count]) => {
      const tag = tags.find(t => t.id === tagId);
      return {
        id: tagId,
        name: tag ? tag.name : '未知标签',
        count
      };
    });
    
    const topTags = tagUsageArray
      .sort((a, b) => b.count - a.count)
      .slice(0, 10);
    
    // 按类别统计标签
    const tagCategories = [];
    const categoryMap = {};
    
    tags.forEach(tag => {
      const category = tag.category || '未分类';
      if (!categoryMap[category]) {
        categoryMap[category] = 0;
      }
      categoryMap[category]++;
    });
    
    const totalTags = tags.length;
    Object.entries(categoryMap).forEach(([name, count]) => {
      tagCategories.push({
        name,
        count,
        percentage: totalTags > 0 ? Math.round((count / totalTags) * 100) : 0
      });
    });
    
    // 按使用频率排序
    tagCategories.sort((a, b) => b.count - a.count);
    
    // 计算平均每个提示词的标签数
    const totalTagsUsed = prompts.reduce((sum, p) => sum + (p.tags ? p.tags.length : 0), 0);
    const tagsPerPrompt = prompts.length > 0 ? (totalTagsUsed / prompts.length).toFixed(1) : 0;
    
    // 计算版本统计数据
    const totalVersions = prompts.reduce((sum, p) => sum + (p.versions ? p.versions.length : 0), 0);
    const avgVersionsPerPrompt = prompts.length > 0 ? (totalVersions / prompts.length).toFixed(1) : 0;
    const promptsWithMultipleVersions = prompts.filter(p => p.versions && p.versions.length > 1).length;
    
    return {
      promptCount: prompts.length,
      tagCount: tags.length,
      folderCount: folders.length,
      topTags,
      tagCategories,
      tagsPerPrompt,
      avgVersionsPerPrompt,
      promptsWithMultipleVersions
    };
  } catch (error) {
    console.error('获取统计数据失败:', error);
    throw error;
  }
};

/**
 * 增加提示词的复制次数
 * @param {string} promptId 提示词ID
 * @returns {Promise} 返回一个Promise，解析为更新后的使用统计
 */
const incrementCopyCount = async (promptId) => {
  try {
    const db = await getDB();
    
    return new Promise((resolve, reject) => {
      const tx = db.transaction(STORES.USAGE_STATS, 'readwrite');
      const store = tx.objectStore(STORES.USAGE_STATS);
      
      // 获取当前使用统计
      const getRequest = store.get(promptId);
      
      getRequest.onsuccess = (event) => {
        let stats = event.target.result;
        
        if (!stats) {
          // 如果不存在，创建新的统计记录
          stats = {
            id: promptId,
            viewCount: 0,
            copyCount: 1,
            lastUsed: new Date().toISOString()
          };
        } else {
          // 如果存在，增加复制次数
          stats.copyCount = (stats.copyCount || 0) + 1;
          stats.lastUsed = new Date().toISOString();
        }
        
        // 更新数据库
        const putRequest = store.put(stats);
        
        putRequest.onsuccess = () => {
          resolve(stats);
        };
        
        putRequest.onerror = (error) => {
          console.error('保存复制次数失败:', error);
          reject(error);
        };
      };
      
      getRequest.onerror = (error) => {
        console.error('获取使用统计失败:', error);
        reject(error);
      };
      
      tx.oncomplete = () => {
        console.log('复制次数更新事务完成');
      };
      
      tx.onerror = (error) => {
        console.error('复制次数更新事务失败:', error);
        reject(error);
      };
    });
  } catch (error) {
    console.error('增加提示词复制次数失败:', error);
    throw error;
  }
};

/**
 * 删除并重建数据库
 * 这个函数将删除现有的数据库并重新创建，用于解决版本不匹配问题
 * @returns {Promise} 返回一个Promise，解析为重置结果
 */
const resetDatabase = () => {
  return new Promise((resolve, reject) => {
    console.log('尝试删除现有数据库...');
    
    // 删除现有数据库
    const deleteRequest = indexedDB.deleteDatabase(DB_NAME);
    
    deleteRequest.onsuccess = () => {
      console.log('数据库删除成功，准备重新创建...');
      
      // 重新初始化数据库
      initDB()
        .then(db => {
          console.log('数据库重置成功');
          resolve({ success: true, message: '数据库重置成功' });
        })
        .catch(error => {
          console.error('重新初始化数据库失败:', error);
          reject(error);
        });
    };
    
    deleteRequest.onerror = (event) => {
      console.error('删除数据库失败:', event.target.error);
      reject(event.target.error);
    };
    
    deleteRequest.onblocked = () => {
      console.warn('数据库删除被阻塞，请关闭所有相关标签页后重试');
      reject(new Error('数据库删除被阻塞，请关闭所有相关标签页后重试'));
    };
  });
};

/**
 * 获取所有收藏的提示词
 * @returns {Promise} 返回一个Promise，解析为收藏的提示词数组
 */
const getFavoritePrompts = async () => {
  try {
    console.log('正在获取收藏的提示词...');
    
    // 从提示词存储中获取所有项目
    const allPrompts = await getAllItems(STORES.PROMPTS);
    
    // 过滤出收藏的提示词
    const favoritePrompts = allPrompts.filter(prompt => prompt.isFavorite === true);
    
    console.log(`成功获取 ${favoritePrompts.length} 个收藏的提示词`);
    return favoritePrompts;
  } catch (error) {
    console.error('获取收藏的提示词失败:', {
      message: error.message || '未知错误',
      name: error.name,
      stack: error.stack
    });
    
    // 返回空数组而不是抛出错误
    return [];
  }
};

/**
 * 启用/禁用提示词分享
 * @param {string} promptId 提示词ID
 * @param {boolean} isShared 是否分享
 * @param {string} authorName 作者姓名（可选）
 * @param {string} authorContact 作者联系方式（可选）
 * @returns {Promise} 返回一个Promise，解析为更新结果
 */
const togglePromptSharing = async (promptId, isShared, authorName = '', authorContact = '') => {
  try {
    // 导入分享服务
    const { generateShareId, generateVerificationCode, uploadPromptToCloud, deleteSharedPromptFromCloud } = await import('./shareService.js');
    
    // 获取提示词
    const prompt = await getItem(STORES.PROMPTS, promptId);
    if (!prompt) {
      throw new Error(`提示词ID ${promptId} 不存在`);
    }
    
    // 获取提示词的版本历史
    const versions = await getPromptVersions(promptId);
    const currentVersion = versions.find(v => v.isCurrent) || versions[versions.length - 1];
    
    // 生成或清除分享ID
    let shareId = prompt.shareId || null;
    let verificationCode = prompt.verificationCode || null;
    let cloudUrl = null;
    let shareResult = null;
    
    if (isShared) {
      if (!shareId) {
        // 如果启用分享且没有分享ID，则生成一个
        shareId = generateShareId();
        // 同时生成验证码
        verificationCode = generateVerificationCode();
      } else if (!verificationCode) {
        // 如果有分享ID但没有验证码，生成一个新的验证码
        verificationCode = generateVerificationCode();
      }
      
      // 组装完整的提示词数据用于分享
      const promptDataForShare = {
        ...prompt,
        shareId,
        versions: versions,
        currentVersion: currentVersion
      };
      
      // 上传到腾讯云COS，包含验证码
      shareResult = await uploadPromptToCloud(shareId, promptDataForShare, verificationCode);
      cloudUrl = shareResult.url;
      console.log('提示词分享数据上传成功:', shareResult);
      
    } else if (!isShared && shareId) {
      // 如果禁用分享且有分享ID，则从云端删除
      try {
        await deleteSharedPromptFromCloud(shareId);
        console.log('提示词分享数据已从云端删除');
      } catch (deleteError) {
        console.error('从云端删除分享数据失败:', deleteError);
        // 即使删除失败，仍继续流程，将本地分享状态关闭
      }
      // 禁用分享，清除分享ID
      shareId = null;
    }
    
    // 更新提示词
    const updatedPrompt = {
      ...prompt,
      isShared,
      shareId,
      verificationCode: isShared ? verificationCode : null,
      cloudUrl,
      authorName: isShared ? authorName : null,  // 只有在启用分享时才保存作者信息
      authorContact: isShared ? authorContact : null
    };
    
    await updateItem(STORES.PROMPTS, updatedPrompt);
    return {
      success: true,
      isShared,
      shareId,
      verificationCode,
      cloudUrl
    };
  } catch (error) {
    console.error('切换提示词分享状态失败:', error);
    throw error;
  }
};

// 生成分享ID的函数已移至shareService.js

/**
 * 获取提示词的版本历史
 * @param {string} promptId 提示词ID
 * @returns {Promise<Array>} 返回一个Promise，解析为版本数组
 */
const getPromptVersions = async (promptId) => {
  try {
    // 获取提示词
    const prompt = await getItem(STORES.PROMPTS, promptId);
    if (!prompt) {
      throw new Error(`提示词ID ${promptId} 不存在`);
    }
    
    // 返回版本数组，如果没有则返回空数组
    return Array.isArray(prompt.versions) ? prompt.versions : [];
  } catch (error) {
    console.error('获取提示词版本历史失败:', error);
    return [];
  }
};

/**
 * 通过分享ID获取提示词
 * @param {string} shareId 分享ID
 * @returns {Promise} 返回一个Promise，解析为提示词对象
 */
const getPromptByShareId = async (shareId) => {
  try {
    // 先从本地数据库查找
    const allPrompts = await getAllItems(STORES.PROMPTS);
    const sharedPrompt = allPrompts.find(p => p.shareId === shareId && p.isShared);
    
    if (sharedPrompt) {
      return sharedPrompt;
    }
    
    // 如果本地没有，则返回null，因为需要验证码才能从云端获取
    return null;
  } catch (error) {
    console.error('通过分享ID获取提示词失败:', error);
    throw error;
  }
};

/**
 * 获取分享提示词的元数据
 * @param {string} shareId 分享ID
 * @returns {Promise<Object>} 提示词元数据
 */
const getSharedPromptMetadata = async (shareId) => {
  try {
    // 先从本地数据库查找
    const allPrompts = await getAllItems(STORES.PROMPTS);
    const sharedPrompt = allPrompts.find(p => p.shareId === shareId && p.isShared);
    
    if (sharedPrompt) {
      return { exists: true, isLocal: true };
    }
    
    // 如果本地没有，则从腾讯云COS获取元数据
    try {
      const { getSharedPromptMetadata } = await import('./shareService.js');
      const metadata = await getSharedPromptMetadata(shareId);
      return { ...metadata, isLocal: false };
    } catch (cloudError) {
      console.error('从云端获取分享提示词元数据失败:', cloudError);
      return { exists: false };
    }
  } catch (error) {
    console.error('获取分享提示词元数据失败:', error);
    throw error;
  }
};

/**
 * 验证并获取分享的提示词
 * @param {string} shareId 分享ID
 * @param {string} verificationCode 验证码
 * @returns {Promise<Object>} 提示词数据
 */
const verifyAndGetSharedPrompt = async (shareId, verificationCode) => {
  try {
    // 先从本地数据库查找
    const allPrompts = await getAllItems(STORES.PROMPTS);
    const sharedPrompt = allPrompts.find(p => p.shareId === shareId && p.isShared);
    
    if (sharedPrompt) {
      // 本地提示词不需要验证码
      return sharedPrompt;
    }
    
    // 如果本地没有，则从腾讯云COS获取并验证
    try {
      const { verifyAndGetSharedPrompt } = await import('./shareService.js');
      const cloudPrompt = await verifyAndGetSharedPrompt(shareId, verificationCode);
      return cloudPrompt;
    } catch (cloudError) {
      console.error('验证并获取分享提示词失败:', cloudError);
      throw cloudError;
    }
  } catch (error) {
    console.error('验证并获取分享提示词失败:', error);
    throw error;
  }
};

/**
 * 导出常量和方法
 */
const DB = {
  STORES,
  initDB,
  getDB,
  migrateFromLocalStorage,
  saveItem,
  saveAllItems,
  getAllItems,
  getItem,
  deleteItem,
  clearStore,
  updateItem,
  recordPromptUsage,
  getPromptUsageStats,
  getAllPromptUsageStats,
  getMostUsedPrompts,
  getWeeklyHeatmapData,
  getStats,
  incrementCopyCount,
  resetDatabase,
  getFavoritePrompts,
  togglePromptSharing,
  getPromptByShareId,
  getSharedPromptMetadata,
  verifyAndGetSharedPrompt,
  getPromptVersions
};

export default DB;
